import * as React from 'react';
import Loader from 'components/Loader';
import { isValidSessionID } from 'utils/session';
import ErrorBoundary from '../ErrorBoundary';
import { CustomerFacingError } from 'utils/ui/errors';
import { LoaderFunctionArgs, RouteObject } from 'react-router';
import { IRoutingContext } from 'router/context';
import { loadPublicSession, userSession } from 'state/session';
import { getLocale } from 'i18n/locale';
import { IBrand, IMarket } from 'data/schema';
import { EErrorCode } from 'config/errors-codes';
import CustomerEntry from 'containers/CustomerEntry';
import { initSessionBroadcasting } from 'utils/ui/rtc';
import { tenantConfig } from 'utils/tenant';

const Stream = React.lazy(() => import('containers/Stream'));
const Legal = React.lazy(() => import('containers/LegalDocuments/Legal'));
const Imprint = React.lazy(() => import('containers/LegalDocuments/Imprint'));
const EndSession = React.lazy(() => import('containers/EndSession'));

async function contextLoader({ params }: LoaderFunctionArgs): Promise<IRoutingContext> {
  const { brand, market, sessionId } = params;
  userSession().setValues({
    brand: brand as IBrand,
    market: market as IMarket,
    locale: getLocale(market as IMarket),
  });
  if (sessionId && isValidSessionID(sessionId) && userSession().get('id') !== sessionId) {
    await loadPublicSession(sessionId);
  }
  return { userType: 'customer' };
}

async function customerSessionLoader(loaderOptions: LoaderFunctionArgs) {
  const { sessionId } = loaderOptions.params;
  if (!isValidSessionID(sessionId)) {
    console.log('Invalid session ID:', sessionId, typeof sessionId);
    throw new CustomerFacingError(EErrorCode.INVALID_SESSION_ID);
  }
  try {
    userSession().once('change', () => {
      initSessionBroadcasting();
    });
  } catch {
    throw new CustomerFacingError(EErrorCode.CANT_LOAD_SESSION);
  }

  // do not proceed until config is loaded
  await tenantConfig().configLoaded;

  return { userType: 'customer' };
}

const localizedRoutes: RouteObject[] = [
  {
    path: '/:brand/:market/view/:sessionId/goodbye',
    element: (
      <React.Suspense fallback={<Loader />}>
        <EndSession />
      </React.Suspense>
    ),
  },
  {
    path: '/:brand/:market/view',
    element: <CustomerEntry />,
    loader: customerSessionLoader,
    children: [
      {
        path: ':sessionId',
        element: (
          <React.Suspense fallback={<Loader />}>
            <Stream expanded />
          </React.Suspense>
        ),
      },
    ],
  },
  {
    path: '/:brand/:market/legal',
    element: (
      <React.Suspense fallback={<Loader />}>
        <Legal />
      </React.Suspense>
    ),
  },
  {
    path: '/:brand/:market/imprint',
    element: (
      <React.Suspense fallback={<Loader />}>
        <Imprint />
      </React.Suspense>
    ),
  },

  // add error boundary to all customer routes
].map((route) => ({ ...route, errorElement: <ErrorBoundary /> }));

export const customerRoutes: RouteObject[] = [
  {
    path: '/:brand/:market',
    children: localizedRoutes,
    loader: contextLoader,
  },
];
